/**
 * The clinic's built-in "Other" supplier, for small one-off suppliers. The
 * server creates it and lists it first; purchases and orders against it
 * carry the actual supplier's name (required) and phone (optional), so a
 * due on "Other" still says who is owed.
 */

export interface OtherSupplierForm {
  /** Whether the picked supplier is Other. Kept on the form rather than
   * looked up in the dropdown list, which a search can narrow so that the
   * picked row is no longer in it. */
  supplierIsOther?: boolean;
  otherSupplierName?: string | null;
  otherSupplierPhone?: string | null;
}

/**
 * Form fields after the supplier dropdown changes. InputDropSearch hands
 * back the whole supplier row as value2. A new supplier clears any name
 * typed for the old one.
 */
export const otherSupplierPick = (target: { value2?: any }) => ({
  supplierIsOther: Boolean(target?.value2?.isOther),
  otherSupplierName: "",
  otherSupplierPhone: "",
});

/** A stored purchase or order is against Other exactly when it has a typed name. */
export const otherSupplierFromRecord = (record: any): OtherSupplierForm => ({
  supplierIsOther: Boolean(record?.otherSupplierName),
  otherSupplierName: record?.otherSupplierName ?? "",
  otherSupplierPhone: record?.otherSupplierPhone ?? "",
});

/** Other was picked but nobody said who the supplier actually is. */
export const isOtherSupplierNameMissing = (form: OtherSupplierForm) =>
  Boolean(form?.supplierIsOther) &&
  !String(form?.otherSupplierName ?? "").trim();

/** The typed fields to send, trimmed; empty for a real supplier. */
export const otherSupplierPayload = (form: OtherSupplierForm) =>
  form?.supplierIsOther
    ? {
        otherSupplierName: String(form.otherSupplierName ?? "").trim(),
        otherSupplierPhone: String(form.otherSupplierPhone ?? "").trim(),
      }
    : { otherSupplierName: null, otherSupplierPhone: null };

/** An open order in the invoice's order picker; Other orders say who for. */
export const describeOrderOption = (order: any) =>
  [order?.orderNo, order?.otherSupplierName].filter(Boolean).join(" · ") +
  (order?.orderDate ? ` — ${order.orderDate}` : "");
